import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import { Button, Divider } from '@mui/material';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import MuiAlert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useCompanies from './Companies.services';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const CompanyDetail = () => {

  const { id } = useParams();
  const [openDialog, setOpenDialog] = useState(false);
  const [openSubItemDialog, setOpenSubItemDialog] = useState(false);
  const [cardEditing, setCardEditing] = useState({ id: -1, name: ""});
  const [cardSubItemEditing, setCardSubItemEditing] = useState({ parentId: -1, id: -1, name: ""});

  const {
    companyDetail,
    getCompany,
    updateName,
    updateRFC,
    updateRepresentante,
    updateRepresentanteTrabajadores,
    updateOccupationArea,
    updateOccupationSubarea,
    occupationAreas,
    getOccupationAreasCatalog,
    updateSuccess,
    updateFailure,
    updateCompany,
    originalDataChanged,
    handleClose,
  } = useCompanies(id);

  useEffect(() => {
    getCompany(id);
    getOccupationAreasCatalog();
  }, []);

  return (
    <Box
      component="main"
      sx={{
        backgroundColor: (theme) =>
          theme.palette.mode === 'light'
            ? theme.palette.grey[100]
            : theme.palette.grey[900],
        flexGrow: 1,
        height: '100vh',
        overflow: 'auto',
      }}
    >
      <Toolbar />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          {/* Recent Orders */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>
                Datos generales
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    required
                    id="name"
                    name="name"
                    label="Nombre de la compañía"
                    fullWidth
                    autoComplete="given-name"
                    variant="standard"
                    value={companyDetail.name}
                    onChange={(event) => {
                      updateName(event.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="rfc"
                    name="rfc"
                    label="RFC"
                    fullWidth
                    inputProps={{ maxLength: 13 }}
                    helperText="Máximo 13 caracteres"
                    autoComplete="off"
                    variant="standard"
                    value={companyDetail.rfc ?? ""}
                    onChange={(event) => {
                      updateRFC(event.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="representante"
                    name="representante"
                    label="Representante"
                    fullWidth
                    autoComplete="off"
                    variant="standard"
                    value={companyDetail.representante ?? ""}
                    onChange={(event) => {
                      updateRepresentante(event.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="representanteTrabajadores"
                    name="representanteTrabajadores"
                    label="Representante de los trabajadores (opcional)"
                    fullWidth
                    autoComplete="off"
                    variant="standard"
                    value={companyDetail.representanteTrabajadores ?? ""}
                    onChange={(event) => {
                      updateRepresentanteTrabajadores(event.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    id="occupationArea"
                    options={occupationAreas}
                    getOptionLabel={(option) => option?.name ?? ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={companyDetail.occupationArea}
                    onChange={(event, newValue) => {
                      updateOccupationArea(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} required label="Área" variant="standard" />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    id="occupationSubarea"
                    options={companyDetail.occupationArea?.subareas ?? []}
                    getOptionLabel={(option) => option?.name ?? ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={companyDetail.occupationSubarea}
                    disabled={!companyDetail.occupationArea}
                    onChange={(event, newValue) => {
                      updateOccupationSubarea(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} required label="Subárea" variant="standard" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Divider />
                  <Button
                    disabled={!originalDataChanged || !companyDetail.occupationArea || !companyDetail.occupationSubarea}
                    variant="contained"
                    sx={{ mt: 3, ml: 1 }}
                    onClick={() => { updateCompany() }}
                  >
                    Actualizar
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>
      <Snackbar open={updateSuccess} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success" sx={{ width: '100%' }}>
          Compañía actualizada
        </Alert>
      </Snackbar>
      <Snackbar open={updateFailure} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error" sx={{ width: '100%' }}>
          Error, no se pudo actualizar los datos de la compañía
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyDetail;